/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * useHighlight.ts — us\1 \2ighlight (services/operator-web-app).
 */

import { useEffect } from 'react';
import { useOperatorStore } from '@/store';
import type { HighlightWidget } from '@/store/slices/copilotSlice';

export interface UseHighlightResult {
  isHighlighted: boolean;
  reason: string | null;
  level: 'soft' | 'medium' | 'critical' | null;
  clearHighlight: () => void;
}

/**
 * E6-S3: Hook for contextual UI highlighting. Returns whether this widget is the current
 * highlight target, reason/level, and clearHighlight. Auto-clears when TTL expires.
 * No forced navigation (caller does not scroll).
 */
export function useHighlight(widgetId: HighlightWidget): UseHighlightResult {
  const highlight = useOperatorStore((s) => s.copilot.highlight);
  const clearHighlight = useOperatorStore((s) => s.clearHighlight);

  const now = Date.now();
  const isActive =
    highlight != null && highlight.targetWidget === widgetId && now <= highlight.expiresAt;

  useEffect(() => {
    if (!highlight || highlight.targetWidget !== widgetId) return;
    const t = highlight.expiresAt - Date.now();
    if (t <= 0) {
      clearHighlight();
      return;
    }
    const id = setTimeout(clearHighlight, t);
    return () => clearTimeout(id);
  }, [highlight, widgetId, clearHighlight]);

  return {
    isHighlighted: !!isActive,
    reason: isActive ? highlight!.reason : null,
    level: isActive ? highlight!.level : null,
    clearHighlight,
  };
}
